/**
 * משתמשי הארגון — user administration. OA-0001 §3.2 / §3.7, 01 §A3, 02 §8, 04 §8.2.
 *
 * ── THE COLUMN THAT CARRIES THE ARCHITECTURE ────────────────────────────────────────────────────
 *
 * `תפקידים` is legitimately EMPTY for an invited user, and the screen writes that emptiness out in
 * words rather than leaving a blank cell. OA-0001 §3.7: an invitation carries NO ROLE, so an invitee
 * arrives with no capabilities at all. A grid that rendered an empty cell there would be describing
 * a missing value; this one is describing a deliberate state.
 *
 * ── DISABLING IS A STATUS, NEVER A DELETE — AND IT NOW HAS AN INVERSE ───────────────────────────
 *
 * 02 §8: no physical delete. A disabled membership keeps its grants resolvable and its audit history
 * attributable, which is the whole reason an audit trail can still answer "who did this" about
 * somebody who left last year. So the action is `השבתה`, the row stays, and 01 §A3's seat rule is
 * visible beside it: only `invited` and `active` consume a seat.
 *
 * What was missing was the way BACK. `disable` had no inverse, so it was a one-way door and every
 * mistake needed somebody with database access. `הפעלה מחדש` is now beside it — and it restores a
 * STATUS and nothing else: the route deliberately carries no `roleCodes`, because a user who comes
 * back comes back with what they had, and changing that is a separate call under a separate
 * capability against a separate table. It restores no sessions either, and the screen says so rather
 * than leaving somebody to assume their colleague is signed in again.
 *
 * ── AN INVITATION THAT LANDED NOWHERE IS RECOVERABLE ───────────────────────────────────────────
 *
 * `שליחה חוזרת` mints a NEW token and the previous one stops resolving. Both halves are stated,
 * because an office that sent two links needs to know that only the last one works. And when the mail
 * port suppressed or refused the message the answer is a 202 carrying `delivery` — the office is told
 * the invitation EXISTS and the message did not go, which is the difference between waiting and
 * telephoning.
 *
 * ── AND READING A COLLEAGUE'S SESSIONS IS SURVEILLANCE, WHICH IS WHY IT IS AUDITED ─────────────
 *
 * `tenant.user.session.read` is `audit: 'ALWAYS'` even though it writes nothing: 04 §8.2's obligation
 * covers what was LOOKED AT as much as what was changed. The screen says so at the point of use, and
 * the payload carries no address, no device and no location — nothing that would reward the look.
 *
 * ── THE MODALS ARE MODALS ──────────────────────────────────────────────────────────────────────
 *
 * 09 §2.1: one decision, one field, no table, no drill-down, and each states its blast radius by
 * name. `Modal.tsx` enforces those mechanically; these are the cases they were written for.
 *
 * ── AND THE ROWS NOW COME FROM THE SERVER. **Phase 2 §6, vertical A.** ─────────────────────────
 *
 * This screen used to render `fixturesEnabled() ? fixtureMemberships : []`, which meant it showed
 * development data to a developer and an EMPTY OFFICE to everybody else — the same blank table
 * whether the office had no users, whether the gate refused, or whether nothing was reachable.
 *
 * `tenant.member.read` now supplies the rows through `<ServerRead>`, which keeps those apart. The
 * two secondary reads are deliberately separate calls rather than fields bolted onto the first:
 *
 *   · `tenant.role.read` supplies the Hebrew NAMES for role codes. It DEGRADES rather than blocks —
 *     if it fails the codes render raw, because nobody can administer an office while a lookup table
 *     is unavailable, and the codes are still true.
 *   · `tenant.user.session.read` is fetched by `<MemberSessions>`, which mounts only when somebody
 *     opens the panel. That is not a performance choice: the route is `audit: 'ALWAYS'`, and reading
 *     a colleague's sessions on every page load would write a surveillance record about an
 *     administrator who never looked.
 *
 * Every mutation ends in `members.reload()`. A screen that patched its local array would drift from
 * the server the moment two administrators worked at once.
 */

import { useCallback, useMemo, useState } from 'react';
import { useLocale } from '../i18n/LocaleProvider';
import { AppShell } from '../shell/AppShell';
import { useWorkContext } from '../shell/ContextProvider';
import { ActionFailureNotice } from '../components/ActionFailureNotice';
import { DataTable } from '../components/DataTable';
import { Modal } from '../components/Modal';
import { ServerRead } from '../components/ServerRead';
import { StateBlock } from '../components/StateBlock';
import { StatusChip } from '../components/StatusChip';
import { SurfaceNotice } from '../components/SurfaceNotice';
import type { Column } from '../components/DataTable';
import type { AccessLevel, MailDeliveryReason, MembershipSummary } from '../api/contract';
import { ACCESS_LEVELS, MAIL_DELIVERY_REASONS } from '../api/contract';
import { accessLevelHe, membershipStatusHe, roleLabel } from '../api/adminVocabulary';
import { deliveryTextFor } from '../api/deliveryText';
import {
  adminRevocationChoices,
  revocationReasonRefusalText,
  sessionRowText,
  sessionSummaryHe,
} from '../api/sessionText';
import type { ActionFailure } from '../api/actionOutcome';
import { classifyActionError } from '../api/actionOutcome';
import { callOperation } from '../api/operations';
import type { OperationResults } from '../api/operations';
import { listOf, useServerData } from '../api/useServerData';
import { routes } from '../routes';


/** Which modal is open. One at a time: each is one decision (09 §2.1 M3). */
type Pending =
  | { readonly kind: 'disable'; readonly member: MembershipSummary }
  | { readonly kind: 'reactivate'; readonly member: MembershipSummary }
  /*
   * ── A RESEND IS ABOUT AN INVITATION, AND USED TO CARRY A MEMBERSHIP. **2026-10-28.** ─────
   *
   * The control sat on a members row and passed `member.membershipUid` as `invitation_uid`. A
   * pending invitation HAS no membership — that row is created when the person redeems — so the
   * only rows it could ever appear on were people who had already joined, for whom a resend means
   * nothing, and the value it would have sent was the wrong identifier anyway. It was gated on
   * `status === 'invited'`, a membership status the schema admits and NOTHING IN THIS PRODUCT EVER
   * WRITES, so in practice the button never rendered at all: the Owner looked for it and reported
   * it missing.
   *
   * It now carries the invitation, from the list below, and sends that invitation's own uid.
   */
  | { readonly kind: 'resend'; readonly invitation: InvitationRow }
  | { readonly kind: 'revokeSessions'; readonly member: MembershipSummary };

/** One row of `tenant.invitation.read`, named so the modal and the table agree about its shape. */
type InvitationRow = OperationResults['tenant.invitation.read']['invitations'][number];

/**
 * The level in office Hebrew, or the raw value when it is not one of the three.
 *
 * The wire types `accessLevel` as a string because it comes out of a column, and `accessLevelHe`
 * takes the closed union. A value outside it would be a row no vocabulary describes, so it is
 * SHOWN AS IT IS rather than mapped to a plausible one — an invented label on an unknown level is
 * the shape 07 N-5 records, where a missing value became a working default.
 */
function levelText(level: string): string {
  return (ACCESS_LEVELS as readonly string[]).includes(level) ? accessLevelHe(level as AccessLevel) : level;
}

type Outcome =
  | { readonly kind: 'disabled'; readonly name: string }
  | { readonly kind: 'reactivated'; readonly name: string }
  | { readonly kind: 'resent'; readonly delivery: MailDeliveryReason | null }
  | { readonly kind: 'sessionsRevoked'; readonly count: number }
  | { readonly kind: 'failed'; readonly failure: ActionFailure };

function isDeliveryReason(value: unknown): value is MailDeliveryReason {
  return typeof value === 'string' && (MAIL_DELIVERY_REASONS as readonly string[]).includes(value);
}

export function TenantUsersScreen(): JSX.Element {
  const { t } = useLocale();

  const ctx = useWorkContext();
  const [pending, setPending] = useState<Pending | null>(null);
  const [reason, setReason] = useState('');
  const [revocationReason, setRevocationReason] = useState(
    adminRevocationChoices()[0]?.reason ?? 'administrator_revoked',
  );
  const [viewingSessionsOf, setViewingSessionsOf] = useState<MembershipSummary | null>(null);
  const [sending, setSending] = useState(false);
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  const readMembers = useCallback(() => callOperation('tenant.member.read', {}), []);
  const readRoles = useCallback(() => callOperation('tenant.role.read', {}), []);
  /*
   * THE INVITATIONS, 2026-10-28. A SEPARATE READ AND A SEPARATE LIST, deliberately: an invitation
   * and a membership are different things with different identifiers and different actions, and
   * merging them into one table would mean inventing a row type that is neither — which is how the
   * resend came to be keyed by the wrong uid in the first place.
   */
  const readInvitations = useCallback(() => callOperation('tenant.invitation.read', {}), []);
  const members = useServerData(readMembers);
  const roles = useServerData(readRoles);
  const invitations = useServerData(readInvitations);

  // The names are a LOOKUP, so a failure here degrades to the codes rather than blocking the screen.
  // `listOf` yields nothing while loading or after a failure, and `roleNames.get(code) ?? code` in
  // the column below is what makes that survivable.
  const roleNames = useMemo(
    () => new Map(listOf(roles.result, 'roles').map((r) => [r.roleCode, roleLabel(r)])),
    [roles.result],
  );

  if (ctx.surface !== 'TENANT') {
    return <SurfaceNotice titleHe={t('users.title')} requires="TENANT" actual={ctx.surface} />;
  }

  function close(): void {
    setPending(null);
    setReason('');
  }

  async function run(action: () => Promise<Outcome>): Promise<void> {
    setSending(true);
    setOutcome(null);
    try {
      setOutcome(await action());
      // RE-READ, never patch. Two administrators working at once is the ordinary case in an office,
      // and a locally-patched array would show one of them a table that no longer exists.
      members.reload();
      // AND THE INVITATIONS: a resend mints a new token and moves the expiry, so the row the
      // administrator is looking at is stale the moment the call returns.
      invitations.reload();
    } catch (err: unknown) {
      setOutcome({ kind: 'failed', failure: classifyActionError(err) });
    } finally {
      setSending(false);
      close();
    }
  }

  function confirm(target: Pending): void {
    void run(async () => {
      if (target.kind === 'disable') {
        await callOperation('tenant.user.disable', {
          body: { membershipUid: target.member.membershipUid, reason },
        });
        return { kind: 'disabled', name: target.member.displayName };
      }
      if (target.kind === 'reactivate') {
        // NO `roleCodes`. Reactivation restores a STATUS; what the user may do is a separate call.
        await callOperation('tenant.user.reactivate', {
          body: { membershipUid: target.member.membershipUid, reason },
        });
        return { kind: 'reactivated', name: target.member.displayName };
      }
      if (target.kind === 'resend') {
        // 202 with `delivery` when the message did not go out. The invitation still exists.
        // THE INVITATION'S OWN UID. It was `member.membershipUid` — see the `Pending` union.
        const result = await callOperation('tenant.user.invitation.resend', {
          params: { invitation_uid: target.invitation.invitationUid },
          body: {},
        });
        return {
          kind: 'resent',
          delivery: isDeliveryReason(result?.delivery) ? result.delivery : null,
        };
      }
      const result = await callOperation('tenant.user.session.revoke', {
        params: { membership_uid: target.member.membershipUid },
        // `sessionUid` ABSENT means every session of that member — the shape of the request, never a
        // boolean called `all` that defaults.
        body: { reason: revocationReason },
      });
      return { kind: 'sessionsRevoked', count: result?.revoked ?? 0 };
    });
  }

  const columns: readonly Column<MembershipSummary>[] = [
    {
      key: 'name',
      headerHe: t('users.name'),
      render: (m) => (
        <span className="company-cell">
          <span className="company-cell__name">{m.displayName}</span>
          <span className="company-cell__id" dir="ltr">
            {m.email}
          </span>
        </span>
      ),
    },
    {
      key: 'status',
      headerHe: t('users.status'),
      render: (m) => {
        const term = membershipStatusHe(m.status);
        return <StatusChip labelHe={term.labelHe} tone={term.tone} />;
      },
    },
    {
      key: 'roles',
      headerHe: t('users.roles'),
      render: (m) =>
        m.roleCodes.length === 0 ? (
          // OA-0001 §3.7 written out. Not a blank cell: a blank cell reads as missing data.
          <span className="cell-note">{t('roles.noneInvited')}</span>
        ) : (
          <span className="chip-row">
            {m.roleCodes.map((code) => (
              <span key={code} className="chip">
                {roleNames.get(code) ?? code}
              </span>
            ))}
          </span>
        ),
    },
    {
      key: 'seat',
      headerHe: t('users.consumesSeat'),
      render: (m) => (m.consumesSeat ? t('common.yes') : t('common.no')),
    },
    {
      key: 'actions',
      headerHe: t('common.actions'),
      // Every action a membership has, and each one appears where its state makes it meaningful. A
      // hidden control is presentation and never a permission decision — the gate refuses regardless.
      render: (m) => (
        <span className="chip-row">
          {/*
            NO RESEND HERE. It was gated on `status === 'invited'`, which nothing in this product
            writes, and it passed a membership uid where an invitation uid belongs. A resend belongs
            to the invitations list below, where the row HAS an invitation uid — *"לא להפנות
            לכפתור שליחה חוזרת שאינו קיים או להזמנה שהמשתמש אינו רואה"*.
          */}
          {m.status === 'disabled' ? (
            <button
              type="button"
              className="btn btn--quiet"
              onClick={() => setPending({ kind: 'reactivate', member: m })}
            >
              {t('users.reactivate')}
            </button>
          ) : (
            <button
              type="button"
              className="btn btn--quiet"
              onClick={() => setPending({ kind: 'disable', member: m })}
            >
              {t('users.disable')}
            </button>
          )}
          <button type="button" className="btn btn--quiet" onClick={() => setViewingSessionsOf(m)}>
            {t('users.sessions')}
          </button>
        </span>
      ),
    },
  ];

  /**
   * THE INVITATION COLUMNS. **2026-10-28.**
   *
   * Four facts and one action, which is what the Owner asked for: who it went to, what state it is
   * in, when it expires, what is waiting to be applied, and a resend.
   *
   * THE DATE IS RENDERED BY THE BROWSER'S OWN LOCALE FORMATTER over the ISO string the server sent.
   * No format is invented here: a hand-built `dd/mm/yyyy` is a second date format in a product that
   * already has one, and it reads wrongly in the English build.
   */
  const invitationColumns: readonly Column<InvitationRow>[] = [
    {
      key: 'recipient',
      headerHe: t('invitations.recipient'),
      render: (row) => (
        <span className="company-cell">
          <span className="company-cell__name">{row.displayName}</span>
          <span className="company-cell__id" dir="ltr">
            {row.email}
          </span>
        </span>
      ),
    },
    {
      key: 'status',
      headerHe: t('users.status'),
      render: (row) => {
        /*
         * THREE STATES, EACH NAMED. A revoked invitation and an expired one are different facts
         * with different remedies, and an address whose invitation has quietly gone is the
         * experience the Owner reported. `caution` rather than `danger` for both: neither is a
         * failure, and the address can be invited again.
         */
        const label =
          row.status === 'revoked'
            ? t('invitations.statusRevoked')
            : row.status === 'expired'
              ? t('invitations.statusExpired')
              : t('invitations.statusIssued');
        /*
         * `warn`, not `error`: neither a revoked nor an expired invitation is a failure, and the
         * address can be invited again. A live one is `ok` — it is doing what it should.
         */
        return <StatusChip labelHe={label} tone={row.status === 'issued' ? 'ok' : 'warn'} />;
      },
    },
    {
      key: 'expires',
      headerHe: t('invitations.expires'),
      render: (row) => <span dir="ltr">{new Date(row.expiresAtUtc).toLocaleDateString()}</span>,
    },
    {
      key: 'waiting',
      headerHe: t('invitations.waiting'),
      /*
       * WHAT WILL BE APPLIED WHEN THEY JOIN, BY NAME. The roles and companies recorded against this
       * invitation (migration 0022) — a uid in front of an administrator is a number. An empty set
       * is written out rather than left blank: *"בלי תפקיד"* is a legitimate choice and a
       * blank cell reads as missing data.
       */
      render: (row) =>
        row.intents.length === 0 ? (
          <span className="cell-note">{t('invitations.waitingNone')}</span>
        ) : (
          <span className="chip-row">
            {row.intents.map((intent, index) => (
              <span key={`${intent.kind}-${intent.roleCode ?? intent.companyUid ?? String(index)}`} className="chip">
                {intent.kind === 'role'
                  ? (intent.roleNameHe ?? intent.roleCode ?? '')
                  : `${intent.companyName ?? intent.companyUid ?? ''}${intent.accessLevel === null ? '' : ` · ${levelText(intent.accessLevel)}`}`}
              </span>
            ))}
          </span>
        ),
    },
    {
      key: 'actions',
      headerHe: t('common.actions'),
      render: (row) =>
        row.status === 'issued' ? (
          <button
            type="button"
            className="btn btn--quiet"
            onClick={() => setPending({ kind: 'resend', invitation: row })}
          >
            {t('users.resend')}
          </button>
        ) : (
          // NOT A HIDDEN CONTROL: the reason is written where the control would have been, because
          // an absent button with no explanation is the thing this whole section is fixing.
          <span className="cell-note">{t('invitations.resendOnlyIssued')}</span>
        ),
    },
  ];

  return (
    <AppShell
      /* Every string here goes through `t()`; `translatedScreens.test.ts` fails the build
         if one Hebrew literal survives, which is what makes this a claim and not a note. */
      translated
      screenId="tenant-users"
      titleHe={t('users.title')}
      backToParent={{ labelHe: t('settings.title'), href: routes.tenantAdmin }}
      /*
        ── THE PRIMARY ACTION SITS BESIDE THE TITLE. **Section 4, 2026-10-27 (restated).** ────────
        Owner: *"להציג כפתור ראשי בולט „הוספת משתמש”, המובחן מכפתורי הניווט"*, and again mid-round:
        *"בבקשה תדאג לכפתור הוספת משתמש ברורה"*.

        It was the first of three buttons in one row, in the same family as the two beside it — so
        the one thing this screen exists to start looked like the two places it links to. The kit's
        own composition puts the screen's action in `top(title, lead, action)`, which is where
        `TenantHomeScreen` already puts its primary destination, with the kit's filled treatment.

        `gp-primary`, NOT `btn--primary`: `actionAffordance.test.ts` rule 2 keeps the navy filled
        button for a WRITE on a `<button>`, and this is a link to a screen. Two filled shapes, two
        meanings, and the rule that keeps them apart is untouched.
      */
      actions={
        <a className="gp-button gp-primary" href={routes.tenantUserInvitation}>
          {t('invite.addUser')}
        </a>
      }
    >
      {/*
        THE TWO LINKS THAT USED TO SIT HERE ARE ON THE BAR ABOVE. **2026-10-28.** Owner: *"זו
        כפילות מיותרת."* תפקידים והרשאות is a tab of this area; company access is reached from the
        person whose access it is, on their own card, where the grant is actually made.
      */}

      <ServerRead result={members.result} whatHe={t('users.reading')}>
        {(data) => (
          <DataTable<MembershipSummary>
            captionHe={t('users.caption')}
            columns={columns}
            rows={data.members}
            rowKey={(m) => m.membershipUid}
            /*
              T8 — THE ROW OPENS THE PERSON. **Section 6, 2026-10-27.** Owner: *"לחיצה על משתמש
              ברשימה צריכה לפתוח כרטיס"*. Everything that was done to a person was done from three
              other screens, each of which asked you to find them again.
            */
            childHref={(m) => routes.tenantUser(m.membershipUid)}
            // 09 §5.1: any "N" figure states which N it is. Both counts are computed from the rows
            // that ARRIVED — a count taken from anywhere else would survive a failed read and put a
            // number on the screen that describes nothing.
            filterSummaryHe={`${t('users.caption')}: ${String(data.members.length)} · ${t('users.consumesSeat')}: ${String(data.members.filter((m) => m.consumesSeat).length)}`}
            emptyStateHe={t('users.empty')}
          />
        )}
      </ServerRead>

      {/*
        ══ PENDING INVITATIONS — 2026-10-28 ═════════════════════════════════════════════

        Owner: *"להציג הזמנות ממתינות לצד משתמשים פעילים או בלשונית ברורה. להציג נמען, מצב,
        תאריך, תפקידים וגישה הממתינים להפעלה."*

        BESIDE the members, in its own table with its own caption, because the two are different
        kinds of thing. Its own `ServerRead` too: a failure to read the invitations must not blank
        the members list, which is the screen's main subject.
      */}
      <ServerRead result={invitations.result} whatHe={t('invitations.reading')}>
        {(data) => (
          <DataTable<InvitationRow>
            captionHe={t('invitations.title')}
            columns={invitationColumns}
            rows={data.invitations}
            rowKey={(row) => row.invitationUid}
            filterSummaryHe={`${t('invitations.caption')}: ${String(data.invitations.length)}`}
            emptyStateHe={t('invitations.none')}
          />
        )}
      </ServerRead>

      {outcome === null ? null : outcome.kind === 'disabled' ? (
        <StateBlock
          titleHe={t('users.disabledTitle')}
          bodyHe={`${outcome.name} — ${t('users.disabledNote')}`}
          nextStep={<p>{t('users.reactivateHint')}</p>}
        />
      ) : outcome.kind === 'reactivated' ? (
        <StateBlock
          titleHe={t('users.reactivatedTitle')}
          bodyHe={`${outcome.name} — ${t('users.reactivatedNote')}`}
          // Said explicitly, because the opposite assumption is the dangerous one.
          nextStep={<p>{t('users.sessionsNotRestored')}</p>}
        />
      ) : outcome.kind === 'resent' ? (
        (() => {
          const text = deliveryTextFor(outcome.delivery);
          return (
            <StateBlock
              titleHe={text.titleHe}
              bodyHe={text.explanationHe}
              tone={text.tone === 'ok' ? 'empty' : 'error'}
              nextStep={<p>{text.nextStepHe}</p>}
            />
          );
        })()
      ) : outcome.kind === 'sessionsRevoked' ? (
        <StateBlock
          titleHe={t('users.sessionsRevokedTitle')}
          // The COUNT, never "ok". An administrator evicting a compromised account needs to know it
          // was four, and a row per session stays in the log with the reason that was recorded.
          bodyHe={`${String(outcome.count)} ${t('users.sessionsRevokedNote')}`}
        />
      ) : (
        <ActionFailureNotice
          failure={outcome.failure}
          /*
           * ── THE SCREEN THE OWNER WAS STUCK ON. **Section 3, 2026-10-25.** ────────────────────
           *
           * `/tenant/users` showed "אימות זהות והמשך" as plain text with nothing to click. Now the
           * refusal carries the code field itself, and this clears it on success so the person is
           * back on the screen they were on — with the members list re-read, because the action
           * that was refused may now have changed it.
           *
           * It does NOT re-submit the refused action automatically. A write that replays itself
           * after an authentication step is a write nobody pressed; the person presses it again,
           * and this time the gate finds a fresh assertion.
           */
          onStepUpSatisfied={() => {
            setOutcome(null);
            members.reload();
          }}
          renderConflict={(failure) => {
            // The one conflict this screen can read: a revocation reason an administrator may not
            // assert. The permitted set travels as `excess`, exactly as a ceiling refusal does.
            if (failure.body.excess === undefined) return null;
            const text = revocationReasonRefusalText(failure.body.excess);
            return (
              <section className="denial" data-tone="blocked" role="alert">
                <h2 className="denial__title">{text.titleHe}</h2>
                <p className="denial__explanation">{text.explanationHe}</p>
                <p className="denial__next-step">{text.nextStepHe}</p>
              </section>
            );
          }}
        />
      )}

      {/* ── A COLLEAGUE'S SESSIONS. A read that is audited, and says so where it is performed. ── */}
      {viewingSessionsOf === null ? null : (
        <section className="notice" data-tone="info">
          <h2 className="notice__title">{`${t('users.sessions')} · ${viewingSessionsOf.displayName}`}</h2>
          <MemberSessions membershipUid={viewingSessionsOf.membershipUid} />

          <fieldset className="field field--choice">
            <legend className="field__label">{t('users.revokeReason')}</legend>
            {/*
              Two choices, not seven. The other reasons are facts the system records about itself, and
              an administrator who could assert one of them would be writing an event that never
              happened into the audit trail.
            */}
            {adminRevocationChoices().map((choice) => (
              <label key={choice.reason} className="choice">
                <input
                  type="radio"
                  name="revocationReason"
                  value={choice.reason}
                  checked={revocationReason === choice.reason}
                  onChange={() => setRevocationReason(choice.reason)}
                />
                <span>
                  {choice.labelHe}
                  <span className="choice__hint">{choice.hintHe}</span>
                </span>
              </label>
            ))}
          </fieldset>

          <div className="actions-row">
            <button
              type="button"
              className="btn"
              disabled={sending}
              onClick={() => setPending({ kind: 'revokeSessions', member: viewingSessionsOf })}
            >
              {t('users.revokeTitle')}
            </button>
            <button type="button" className="btn" onClick={() => setViewingSessionsOf(null)}>
              {t('common.close')}
            </button>
          </div>
        </section>
      )}

      {pending === null ? null : pending.kind === 'disable' ? (
        <Modal
          intentHe={t('users.disableTitle')}
          blastRadiusHe={`${pending.member.displayName} — ${t('users.disableBlast')}`}
          fields={1}
          confirmLabelHe={sending ? t('common.sending') : t('users.disable')}
          onConfirm={() => confirm(pending)}
          onClose={close}
        >
          <label className="field">
            <span className="field__label">{t('users.disableReason')}</span>
            <input
              className="field__input"
              name="disable-reason"
              value={reason}
              onChange={(e) => setReason(e.currentTarget.value)}
            />
          </label>
        </Modal>
      ) : pending.kind === 'reactivate' ? (
        <Modal
          intentHe={t('users.reactivateTitle')}
          blastRadiusHe={`${pending.member.displayName} — ${t('users.reactivateBlast')}`}
          fields={1}
          confirmLabelHe={sending ? t('common.sending') : t('users.reactivate')}
          onConfirm={() => confirm(pending)}
          onClose={close}
        >
          <label className="field">
            <span className="field__label">{t('users.reactivateReason')}</span>
            <input
              className="field__input"
              name="reactivate-reason"
              value={reason}
              onChange={(e) => setReason(e.currentTarget.value)}
            />
            <span className="field__hint">
              {t('users.reactivateRolesNote')}
            </span>
          </label>
        </Modal>
      ) : pending.kind === 'resend' ? (
        <Modal
          intentHe={t('users.resendTitle')}
          /* THE INVITATION'S recipient, since the modal carries the invitation and not a member. */
          blastRadiusHe={`${pending.invitation.displayName} — ${t('users.resendBlast')}`}
          fields={0}
          confirmLabelHe={sending ? t('common.sending') : t('users.resend')}
          onConfirm={() => confirm(pending)}
          onClose={close}
        >
          <p className="field__hint">
            {t('users.resendNoPermissions')}
          </p>
        </Modal>
      ) : (
        <Modal
          intentHe={t('users.revokeTitle')}
          blastRadiusHe={`${pending.member.displayName} — ${t('users.revokeBlast')}`}
          fields={0}
          confirmLabelHe={sending ? t('common.sending') : t('users.revokeSessions')}
          onConfirm={() => confirm(pending)}
          onClose={close}
        >
          <p className="field__hint">
            {t('users.revokeReasonNote')}
          </p>
        </Modal>
      )}
    </AppShell>
  );
}

/**
 * A COLLEAGUE'S SESSIONS, READ ONLY WHEN SOMEBODY ASKS.
 *
 * This is a component rather than a third `useServerData` in the screen, and the reason is the audit
 * obligation: `tenant.user.session.read` is `audit: 'ALWAYS'` (04 §8.2), so a hook that fired on
 * every page load would write a row saying an administrator looked at a colleague's devices when
 * nobody had. Mounting IS the request, and the panel mounts on the click that means it.
 *
 * The failure states matter more here than on most reads. "No sessions" and "we could not tell you"
 * are opposite conclusions about whether a departing employee is still signed in somewhere, and an
 * administrator acting on the wrong one leaves a live session on a laptop that walked out.
 */
function MemberSessions({ membershipUid }: { readonly membershipUid: string }): JSX.Element {
  const { t } = useLocale();

  const read = useCallback(
    () => callOperation('tenant.user.session.read', { params: { membership_uid: membershipUid } }),
    [membershipUid],
  );
  const sessions = useServerData(read);

  return (
    <ServerRead result={sessions.result} whatHe={t('users.readingSessions')} skeletonRows={3}>
      {(data) => (
        <>
          <p className="notice__body">{sessionSummaryHe(data.sessions)}</p>
          {data.sessions.map((session) => {
            const row = sessionRowText(session);
            return (
              <p key={session.sessionUid} className="notice__body">
                {`${row.stateHe} · ${row.startedHe}`}
                {row.lastSeenHe === null ? '' : ` · ${row.lastSeenHe}`}
              </p>
            );
          })}
        </>
      )}
    </ServerRead>
  );
}
